import React, { useEffect, useRef, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";
import { MagnifyingGlass, ArrowLeft, ShieldCheck } from "@phosphor-icons/react";

const PLAN_OPTIONS = [
  { id: "free", label: "Free" },
  { id: "starter", label: "Starter" },
  { id: "pro", label: "Pro" },
  { id: "team", label: "Team" },
];

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [params] = useSearchParams();
  const highlight = (params.get("highlight") || "").toLowerCase();
  const rowRefs = useRef({});

  useEffect(() => { load(); }, []);

  // Scroll to & briefly focus the highlighted row (e.g. after converting a lead)
  useEffect(() => {
    if (!highlight || loading || users.length === 0) return;
    const el = rowRefs.current[highlight];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlight, loading, users]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users", { params: q ? { q } : {} });
      setUsers(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally { setLoading(false); }
  };

  const changePlan = async (u, plan) => {
    if (plan === (u.plan || "free")) return;
    const note = window.prompt(`Send optional note to ${u.email} with this plan change?`, "") || "";
    setSavingId(u.id);
    try {
      await api.put(`/admin/users/${u.id}/plan`, { plan, note });
      toast.success(`${u.email} → ${plan}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update plan");
    } finally { setSavingId(""); }
  };

  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="p-6 md:p-10" data-testid="admin-users-page">
      <Toaster theme="dark" />
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <Link to="/dashboard" className="overline flex items-center gap-1 hover:text-white mb-2"><ArrowLeft weight="bold" size={12} /> back</Link>
          <div className="overline mb-2 flex items-center gap-2"><ShieldCheck size={14} weight="fill" color="var(--accent)" /> // admin · users</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">All accounts</h1>
        </div>
        <div className="flex items-center gap-2 border px-3 py-2 min-w-[280px]" style={{ borderColor: "var(--border)" }}>
          <MagnifyingGlass size={16} weight="bold" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="Search email or name"
            className="bg-transparent outline-none w-full text-sm"
            data-testid="admin-users-search"
          />
          <button onClick={load} data-testid="admin-users-search-btn" className="text-xs uppercase tracking-widest font-mono" style={{ color: "var(--accent)" }}>go</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t mb-8" style={{ borderColor: "var(--border)" }}>
        <Stat label="Total users" value={users.length} />
        <Stat label="Free" value={users.filter(u => (u.current_plan || u.plan || "free") === "free").length} />
        <Stat label="Paid" value={users.filter(u => ["starter","pro","team"].includes(u.current_plan || u.plan)).length} />
        <Stat label="Team" value={users.filter(u => (u.current_plan || u.plan) === "team").length} accent />
      </div>

      <div className="border" style={{ borderColor: "var(--border)" }} data-testid="admin-users-table">
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 border-b overline" style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
          <div className="col-span-4">User</div>
          <div className="col-span-2">Plan</div>
          <div className="col-span-2">Usage (mo)</div>
          <div className="col-span-2">Joined</div>
          <div className="col-span-2 text-right">Change plan</div>
        </div>
        {loading && <div className="p-6 text-sm text-[#C7DDE5]">Loading users…</div>}
        {!loading && users.length === 0 && <div className="p-6 text-sm text-[#C7DDE5]">No users yet.</div>}
        {users.map((u, i) => {
          const plan = u.current_plan || u.plan || "free";
          const isHighlighted = highlight && (u.email || "").toLowerCase() === highlight;
          return (
            <div
              key={u.id}
              ref={(el) => { if (el) rowRefs.current[(u.email || "").toLowerCase()] = el; }}
              data-testid={`admin-user-row-${i}`}
              className={`grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-4 items-center transition-colors ${i > 0 ? "border-t" : ""}`}
              style={{
                borderColor: isHighlighted ? "var(--accent)" : "var(--border)",
                background: isHighlighted ? "rgba(45,212,212,0.10)" : undefined,
                borderLeft: isHighlighted ? "3px solid var(--accent)" : undefined,
              }}
            >
              <div className="col-span-4 min-w-0">
                <div className="font-semibold truncate">{u.name || u.email}</div>
                <div className="overline truncate">{u.email}</div>
              </div>
              <div className="col-span-2">
                <span className="tag" style={plan !== "free" ? { color: "var(--accent)", borderColor: "var(--accent)" } : {}}>{plan.toUpperCase()}</span>
              </div>
              <div className="col-span-2 text-xs font-mono" style={{ color: "var(--text-2)" }}>
                {u.usage?.contacts ?? 0}c · {u.usage?.scans ?? 0}s · {u.usage?.emails ?? 0}e
              </div>
              <div className="col-span-2 text-xs" style={{ color: "var(--text-2)" }}>
                {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
              </div>
              <div className="col-span-2 md:text-right">
                <select
                  value={plan}
                  disabled={savingId === u.id}
                  onChange={(e) => changePlan(u, e.target.value)}
                  data-testid={`admin-user-plan-${i}`}
                  className="input-base"
                  style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "6px 10px", fontSize: 12 }}
                >
                  {PLAN_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs mt-6" style={{ color: "var(--text-2)" }}>Plan changes fire an email to the user via Resend. Actions logged in <code>admin_actions</code>.</p>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="border-r border-b p-6" style={{ borderColor: "var(--border)" }}>
      <div className="overline">{label}</div>
      <div className="font-display font-black text-3xl mt-2" style={{ color: accent ? "var(--accent)" : "#fff" }}>{value}</div>
    </div>
  );
}
